/**
 * Minimal interaction contract. Doors, the finish seat, the klapikasa, the
 * kamiina and the pane in the pirtti's window implement it; the system itself
 * has no idea what any of them is.
 *
 * There is one key, and it is F. Everything in the level that answers to a
 * hand answers to that one, so the question a player asks at speed is "is
 * there a prompt?" rather than "which of the two keys is this one?" - and the
 * HUD answers it with the same bracket every time.
 *
 * The bottle is deliberately *not* one of these. It is on E, it is the only
 * thing on E, and it reaches for nothing: pressing it is Kimmo drinking from
 * what is already in his hand, wherever he happens to be standing. That is
 * what lets F stay unambiguous - a key that meant "the door, or else a swig"
 * is a key that costs you a sip you did not want at the one moment the yard is
 * least forgiving.
 */

import type { Vec3 } from '../utils/math';

export interface InteractContext {
  /** Player eye position. */
  eye: Vec3;
  /** Player feet position (cylinder base). */
  feet: Vec3;
  /** Unit look direction. */
  look: Vec3;
  simulationTime: number;
}

export interface Interactable {
  readonly id: string;
  /** Shown in the HUD after `[F]` while this is the focused interactable. */
  readonly prompt: string;
  /** Used to break ties when several things are in range. */
  focusPoint(): Vec3;
  canInteract(context: InteractContext): boolean;
  interact(context: InteractContext): void;
  reset(): void;
}

export class InteractionSystem {
  private readonly items: Interactable[] = [];

  register(item: Interactable): void {
    this.items.push(item);
  }

  reset(): void {
    for (const item of this.items) item.reset();
  }

  /**
   * What F would trigger right now, which is also what the HUD is prompting
   * for. Nearest wins, and nothing in the level puts two of these inside one
   * reach of each other - the klapikasa is at the palju and the doors are not.
   */
  findTarget(context: InteractContext): Interactable | null {
    let best: Interactable | null = null;
    let bestDistance = Infinity;
    for (const item of this.items) {
      if (!item.canInteract(context)) continue;
      const p = item.focusPoint();
      const distance = Math.hypot(p.x - context.eye.x, p.y - context.eye.y, p.z - context.eye.z);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = item;
      }
    }
    return best;
  }
}

/** Shared "am I close enough and looking at it" test. */
export function isLookingAt(
  context: InteractContext,
  point: Vec3,
  maxDistance: number,
  minLookDot: number,
): boolean {
  const dx = point.x - context.eye.x;
  const dy = point.y - context.eye.y;
  const dz = point.z - context.eye.z;
  const distance = Math.hypot(dx, dy, dz);
  if (distance > maxDistance) return false;
  if (distance < 1e-4) return true;
  const dot = (dx * context.look.x + dy * context.look.y + dz * context.look.z) / distance;
  return dot >= minLookDot;
}
